import { css } from '@styled-system/css';
import { Radar, Search, Store } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useCredits } from './useCredits';

// Panneau d'usage des quotas mensuels (données SERVEUR) — page abonnement.

const panel = css({
  display: 'flex',
  alignItems: 'center',
  gap: '8',
  flexWrap: 'wrap',
  paddingY: '5',
  paddingX: '7',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'xl',
  marginBottom: '8',
});

const gauge = css({ display: 'flex', flexDirection: 'column', gap: '2', minWidth: '150px' });

const gaugeLabel = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2',
  fontSize: 'caption',
  color: 'text.tertiary',
});

const gaugeValue = css({
  fontFamily: 'mono',
  fontSize: 'body',
  fontWeight: 'bold',
  color: 'text.primary',
});

const track = css({
  width: '100%',
  height: '5px',
  borderRadius: 'full',
  bg: 'surface.sunken',
  overflow: 'hidden',
});

const trialBadge = css({
  display: 'inline-flex',
  alignItems: 'center',
  paddingY: '1',
  paddingX: '3.5',
  borderRadius: 'full',
  fontSize: 'caption',
  fontWeight: 'medium',
  bg: 'brand.primary/10',
  color: 'brand.primary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'brand.primary/40',
});

const Gauge: FC<{ icon: ReactNode; label: string; used: number; max: number }> = ({ icon, label, used, max }) => {
  const ratio = max === 0 ? 0 : Math.min(1, used / max);
  const color =
    used >= max ? 'var(--colors-error-default)' : ratio > 0.8 ? 'var(--colors-warning-default)' : 'var(--colors-brand-primary)';
  return (
    <div className={gauge}>
      <span className={gaugeLabel}>
        {icon}
        {label}
      </span>
      <span className={gaugeValue}>
        {used}
        <span className={css({ color: 'text.tertiary', fontWeight: 'medium' })}> / {max}</span>
      </span>
      <div className={track}>
        <div style={{ width: `${ratio * 100}%`, height: '100%', background: color, transition: 'width 200ms ease' }} />
      </div>
    </div>
  );
};

export const CreditUsagePanel: FC = () => {
  const { t } = useTranslation();
  const usage = useCredits();

  return (
    <div className={panel}>
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '2' })}>
        <span className={css({ fontSize: 'caption', color: 'text.tertiary' })}>{t('credits.currentPlan')}</span>
        <span className={css({ fontSize: 'body', fontWeight: 'semibold', color: 'text.primary' })}>
          {usage.trialing ? t('credits.freeTrial') : usage.planLabel}
        </span>
        {usage.trialing && (
          <span className={trialBadge}>{t('credits.daysLeft', { days: usage.trialDaysLeft })}</span>
        )}
      </div>

      <Gauge
        icon={<Search size={12} strokeWidth={1.75} />}
        label={t('credits.product')}
        used={usage.used.product}
        max={usage.limits.product}
      />
      <Gauge
        icon={<Store size={12} strokeWidth={1.75} />}
        label={t('credits.vendor')}
        used={usage.used.vendor}
        max={usage.limits.vendor}
      />
      <Gauge
        icon={<Radar size={12} strokeWidth={1.75} />}
        label={t('credits.veilleSlots')}
        used={usage.veille.used}
        max={usage.veille.max}
      />
    </div>
  );
};
